import { useState, useEffect, useRef } from 'react';
import _ from 'underscore';
import EverTrue from 'app';
import { useFluxStore } from '@evertrue/et-flux';
import PropTypes from 'prop-types';
import UrlSource from 'apps/layout/sources/url-source';
import ContactSource from 'apps/contact/sources/contact-source';
import MapSource from 'apps/map/sources/map-source';
import ListContactSource from 'apps/lists/sources/list-contact-source';
import ContactQueryStore from 'apps/contact/stores/contact-query-store';
import FilterStore from 'apps/filters/stores/filter-store';
import MapStore from 'apps/map/stores/map-store';
import ContactFixedTable from 'apps/contact/components/contacts/contact-fixed-table';
import ContactTableToolbar from 'apps/contact/components/contacts/table/contact-table-toolbar';
import MapController from 'apps/map/controllers/map-controller';
import MapContacts from 'apps/map/controllers/map-contacts-controller';
import SortUtils from 'mixins/sort-utils';
import CoreSearchSource from 'apps/core-search/sources/core-search-source';
import CoreSearchStore from 'apps/core-search/stores/core-search-store';
import SelectedContactsStore from 'apps/contact/stores/selected-contacts-store';
import MapTableToggleButton from '../components/map-table-toggle-button';

const TOOLBAR_HEIGHT = 67;
const SELECTED_INDICATOR_HEIGHT = 20;

const contactQueryStoreMap = () => ({
  contacts: ContactQueryStore.getContacts(),
  loading: ContactQueryStore.getLoading(),
});

const selectedContactsStoreMap = () => ({
  showSelectedIndicator: SelectedContactsStore.getShowSelectedIndicator(),
});

const filterStoreMap = () => ({
  loadingFilters: FilterStore.getLoading(),
  filterParams: FilterStore.getActiveFiltersQuery(),
});

const coreSearchStoreMap = () => ({
  activeResultsCount: CoreSearchStore.getActiveResultsCount(),
});

const AllConstituentsController = ({ view = 'table', hashId, totalNumber, style }) => {
  const [isContactListFocused, setIsContactListFocused] = useState(false);
  const [enrichmentLoading, setEnrichmentLoading] = useState(true);

  const constituentsRef = useRef(null);

  const { contacts, loading } = useFluxStore(ContactQueryStore, () => contactQueryStoreMap());

  const { showSelectedIndicator } = useFluxStore(SelectedContactsStore, () => selectedContactsStoreMap());

  const { loadingFilters, filterParams } = useFluxStore(FilterStore, () => filterStoreMap());

  const { activeResultsCount } = useFluxStore(CoreSearchStore, () => coreSearchStoreMap());

  const { selectedContacts } = useFluxStore(SelectedContactsStore, () => ({
    selectedContacts: SelectedContactsStore.getSelectedContacts(),
  }));

  const [selectedContactsChanged, setSelectedContactsChanged] = useState(false);

  useEffect(() => {
    setSelectedContactsChanged(true);
    const timeout = setTimeout(() => setSelectedContactsChanged(false), 500);
    return () => clearTimeout(timeout);
  }, [selectedContacts]);

  useEffect(() => {
    if (!hashId) {
      _.defer(() => ContactSource.initQuery());
    }

    // Sets the event type for Adding to list since we track
    // this from various parts of the app differently
    ListContactSource.setEvent('search_action');
  }, [hashId]);

  /** The below triggers but doesn't work as intented i.e. doesn't highlight the searched text. Commenting instead of 
   * removing it for now. In case anyone wants to use it in the future or a bug is raised, they can uncomment and fix it.
    
  Note: activeGlobalSearch can be fetched using: FilterStore.getActiveFilterByKey('global_search')?.value.
  
  useEffect(() => {
    if (activeGlobalSearch) {
      highlightQuery(activeGlobalSearch);
    }
  }, [activeGlobalSearch]);

  const highlightQuery = (text) => {
    const elements = $(constituentsRef.current).find('.contact-card--name strong');
    const words = _.map(text.trim().split(' '), (str) => str.trim());
    elements.highlight(words, 'text-highlight');
  };
*/

  useEffect(() => {
    if (contacts?.total != null) {
      CoreSearchSource.setResultCount('contacts', contacts.total);
      EverTrue.track.setReferrer('contact');
    }
  }, [contacts?.total]);

  const handleSort = (sort_key, reverse, silent) => {
    let sortParamQuery;
    if (sort_key === 'proximity_sort' || sort_key === 'addresses.location') {
      sortParamQuery = MapStore.getProximitySort(reverse);
    } else {
      sortParamQuery = SortUtils.getQuery(sort_key, reverse)[0];
    }
    UrlSource.updateSort(sortParamQuery, silent);
  };

  const handlePage = (page) => {
    setEnrichmentLoading(true);
    UrlSource.updatePage(page);
  };

  const handleMapTableToggle = (route) => {
    setIsContactListFocused(false);
    MapSource.clearClusters();
    const urlData = EverTrue.UrlManager.toJSON();
    EverTrue.Navigator(route, { trigger: true });
    EverTrue.UrlManager.set(urlData);
  };

  return (
    <div className="all-constituents-controller fixed-page-wrapper" style={style}>
      {!loading && (
        <ContactTableToolbar
          contacts={contacts}
          onPage={handlePage}
          allowAddAll={true}
          totalNumber={totalNumber}
          filterParams={filterParams}
          totalResultsCount={activeResultsCount}
        >
          <MapTableToggleButton view={view} onToggle={handleMapTableToggle} />
        </ContactTableToolbar>
      )}

      <div
        ref={constituentsRef}
        className="fixed-page-wrapper"
        style={{ top: TOOLBAR_HEIGHT + (showSelectedIndicator ? SELECTED_INDICATOR_HEIGHT : 0) }}
      >
        {view === 'map' ? (
          <div>
            <MapContacts
              contacts={contacts}
              loading={loading}
              onSort={handleSort}
              hasActiveFocus={isContactListFocused}
            />

            {!loadingFilters && (
              <MapController
                onSendContactsFocus={() => setIsContactListFocused(true)}
                selectedContactsChanged={selectedContactsChanged}
                onMapChange={() => {
                  if (contacts?.sortProp === 'addresses.location') {
                    UrlSource.updateSort(MapStore.getProximitySort(contacts?.sortReverse));
                  }
                  ContactSource.query();
                }}
              />
            )}
          </div>
        ) : (
          <ContactFixedTable
            contacts={contacts}
            loading={loading}
            onPage={handlePage}
            onSort={handleSort}
            topPosition={showSelectedIndicator ? SELECTED_INDICATOR_HEIGHT : null}
            setEnrichmentLoading={setEnrichmentLoading}
            enrichmentLoading={enrichmentLoading}
          />
        )}
      </div>
    </div>
  );
};

AllConstituentsController.propTypes = {
  view: PropTypes.string,
  hashId: PropTypes.string,
  totalNumber: PropTypes.number,
  style: PropTypes.object,
};

export default AllConstituentsController;
